import {
  ErrorHandler,
  Injectable,
  inject
} from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

export const RELOAD_FLAG = 'velocity:chunk-reload-attempted';

export const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk [\w-]+ failed|ChunkLoadError|error loading dynamically imported module/i;

/**
 * Best-effort stringification of an unknown error so the chunk-failure
 * pattern can match Error instances, plain strings, and anything else.
 * Shared with the NavigationError-level recovery (#963), which sees the
 * same dynamic-import failures wrapped in a router event.
 */
export function extractChunkErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Catches dynamic-import / chunk-load failures and recovers by triggering a single
 * full-page reload.
 *
 * Why a reload (and not an in-page retry):
 * The browser caches the result of a failed dynamic `import()` in the module map for
 * the lifetime of the page. Subsequent imports of the same URL return the same
 * already-rejected promise, so naive retries are no-ops. A full-page reload clears
 * the module map and, combined with `Cache-Control: no-store` on `index.html`, causes
 * the browser to fetch fresh chunk references from origin.
 *
 * The session-scoped reload flag prevents an infinite reload loop if the second
 * attempt also fails.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly swUpdate = inject(SwUpdate, { optional: true });

  handleError(error: unknown): void {
    const message = extractChunkErrorMessage(error);

    if (CHUNK_ERROR_PATTERN.test(message)) {
      this.recover();
      return;
    }

    // Non-chunk error: clear the reload guard so a future chunk error can recover.
    sessionStorage.removeItem(RELOAD_FLAG);
    console.error(error);
  }

  private recover(): void {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      // Already reloaded once this session — escalate, don't loop.
      console.error('ChunkLoadErrorHandler: chunk load failed after reload attempt; surfacing to user.');
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG, '1');

    const reload = (): void => {
      window.location.reload();
    };

    if (this.swUpdate?.isEnabled) {
      this.swUpdate
        .activateUpdate()
        .catch((err: unknown) => {
          console.error('ChunkLoadErrorHandler: activateUpdate failed', err);
        })
        .finally(reload);
      return;
    }
    reload();
  }
}
